import React from "react";
import { useTranslation } from "react-i18next";

import roomImg from "../../assets/images/passangerData/room.png";
import StringInput from "../Inputs/StringInput";
import { PASSENGER_KEYS } from "./PassengersData";
import "./PassengerData.scss";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import Select from "../Inputs/Select";
import DropdownDateInput from "../Inputs/DropdownDateInput";
import AutoFormatBirthDatePicker from "../Inputs/AutoFormatBirthDatePicker";
import {
  KIDS_AGES,
  INFANTS,
  KIDS,
  ADULTS,
  PACKAGES,
  useSearch,
} from "../../contexts/SearchContext";
import { getEnglishOnlyPersonNameError } from "../../utils/passengers/englishOnlyPersonName";
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.locale("en");
dayjs.locale("vi");

const PassengerData = ({
  passengerInfo,
  setPassengerInfo,
  roomName,
  roomIndex,
  allDisabled,
  returnDate,
  isTour = false,
  onGlobalFieldsUpdate, // New prop to update global fields
  /** When true, collect gender and derive LDS paxTitle in the mapper (no MR/MS picker). */
  useGenderDerivedLdsTitles = false,
  /** טיסה בלבד: בלי “חדר”/אייקון מלון, בלי roomName undefined — שפת נוסעים בלבד */
  flightPaxFormMode = false,
  fieldErrors = {},
}) => {
  // Translator
  const { t } = useTranslation();
  const { searchTab } = useSearch();

  const getPassenger = (paxType, innerIndex) => {
    // console.log("paxType", paxType);
    return passengerInfo[paxType][innerIndex];
  };

  const fieldErrorKey = (slot, passengerIndex, field) =>
    `room:${roomIndex}:${slot}:${passengerIndex}:${field}`;

  const getFieldError = (slot, passengerIndex, field) =>
    fieldErrors[fieldErrorKey(slot, passengerIndex, field)];

  const roomCompositionError =
    fieldErrors[`room:${roomIndex}:composition`] || fieldErrors[`room:${roomIndex}`];

  const renderFieldValidation = (slot, passengerIndex, field) => {
    const msg = getFieldError(slot, passengerIndex, field);
    if (!msg) return null;
    return <div className="passenger-field__validation-error">{msg}</div>;
  };

  const renderInput = (id, key, innerIndex, paxType, disabled = false) => {
    const isNameField =
      key === PASSENGER_KEYS.FIRSTNAME || key === PASSENGER_KEYS.LASTNAME;
    const nameError = isNameField
      ? getEnglishOnlyPersonNameError(getPassenger(paxType, innerIndex)[key])
      : null;
    return (
      <>
        <StringInput
          id={id}
          title={t(`completepassengers.${key}`)}
          value={getPassenger(paxType, innerIndex)[key]}
          onValueChange={(newVal) =>
            handleChange(innerIndex, paxType, key, newVal)
          }
          disabled={disabled || allDisabled}
          hideX
          status={nameError ? "error" : undefined}
        />
        {nameError ? (
          <div className="passenger-field__validation-error" role="alert">
            {nameError}
          </div>
        ) : null}
      </>
    );
  };

  const renderGender = (innerIndex, paxType, disabled = false) => {
    const options = [
      { name: t("completepassengers.male"), value: "M" },
      { name: t("completepassengers.female"), value: "F" },
    ];
    return (
      <Select
        id={PASSENGER_KEYS.GENDER}
        options={options}
        className="passenger-select--gender"
        value={getPassenger(paxType, innerIndex)[PASSENGER_KEYS.GENDER]}
        title={t("completepassengers.gender")}
        placeholder={t("completepassengers.gender")}
        onChange={(value) =>
          handleChange(innerIndex, paxType, PASSENGER_KEYS.GENDER, value)
        }
        disabled={disabled || allDisabled}
        hideX
      />
    );
  };

  const renderTitle = (key, innerIndex, paxType, disabled = false) => {
    const optionsAdult = [
      { name: "Mr", value: "MR" },
      { name: "Ms", value: "MS" },
      { name: "Child", value: "CHD" },
      // { name: "INFANT", value: "Infant" },
      // { name: "KID", value: "Kid" },
    ];
    const optionsInfant = [{ name: "INFANT", value: "infant" }];
    const optionsKids = [{ name: "KIDS", value: "kids" }];
    const option =
      paxType === ADULTS
        ? optionsAdult
        : paxType === KIDS
          ? optionsKids
          : optionsInfant;
    return (
      <Select
        id={key}
        options={option}
        className="passenger-select--title"
        value={getPassenger(paxType, innerIndex)[key]}
        title={key ? t(`completepassengers.${key}`) : ""}
        placeholder={getPassenger(paxType, innerIndex)[key] || option[0].value}
        onChange={(value) => handleChange(innerIndex, paxType, key, value)}
        disabled={disabled || allDisabled}
        hideX
      />
    );
  };

  const renderDatePickerInput = (
    key,
    innerIndex,
    paxType,
    minAge,
    maxAge,
    disabled = false
  ) => {
    const value = getPassenger(paxType, innerIndex)[key];

    return (
      <div
        className={
          "passenger-field passenger-field--dob" +
          (getFieldError(paxType, innerIndex, key) ? " passenger-field--has-validation-error" : "")
        }
        data-lds-pax-room={roomIndex}
        data-lds-pax-slot={paxType}
        data-lds-pax-index={innerIndex}
        data-lds-pax-field={key}
      >
        <div className="passenger-field__label">{t(`completepassengers.${key}`)}</div>
        <div className="passenger-field__control passenger-field__control--datepicker">
          <AutoFormatBirthDatePicker
            pickerKey={`${key}-${innerIndex}-${paxType}`}
            title={t(`completepassengers.${key}`)}
            returnDate={returnDate}
            minAge={minAge}
            maxAge={maxAge}
            disabled={disabled}
            value={value}
            onChange={(dateStrings) => {
              handleChange(innerIndex, paxType, key, dateStrings);
            }}
          />
        </div>
        {renderFieldValidation(paxType, innerIndex, key)}
      </div>
    );
  };

  function handleChange(innerIndex, paxType, key, value) {
    let tempPassengerInfo = JSON.parse(JSON.stringify(passengerInfo));
    const previousValue = tempPassengerInfo[paxType][innerIndex][key];
    tempPassengerInfo[paxType][innerIndex][key] = value;
    
    // Auto-sync email and phone from passenger 1 to all other passengers (only for packages)
    if (
      searchTab === PACKAGES &&
      paxType === ADULTS &&
      (key === PASSENGER_KEYS.EMAIL || key === PASSENGER_KEYS.CELL) &&
      innerIndex === 0 &&
      tempPassengerInfo[ADULTS] &&
      tempPassengerInfo[ADULTS].length > 1
    ) {
      // Update all other passengers if their value matches the previous value of passenger 1
      // This means they were auto-filled and should be updated
      // If they have a different value, the user manually changed it, so don't update
      for (let i = 1; i < tempPassengerInfo[ADULTS].length; i++) {
        const otherPassenger = tempPassengerInfo[ADULTS][i];
        // If the other passenger's value is empty or matches the previous value of passenger 1,
        // update it to the new value (they were auto-filled)
        // If they have a different value, the user manually changed it, so don't update
        if (
          !otherPassenger[key] ||
          otherPassenger[key] === previousValue
        ) {
          otherPassenger[key] = value;
        }
      }
    }
    
    setPassengerInfo(tempPassengerInfo);
    
    // Update global fields if phone or email changed for first adult
    if (paxType === 'adults' && innerIndex === 0 && (key === 'cell' || key === 'email')) {
      if (onGlobalFieldsUpdate) {
        onGlobalFieldsUpdate(key, value);
      }
    }
  }

  const renderKids = () => {
    return passengerInfo?.kids?.map((_, kidInnerIndex) => (
      <div className={"passenger-container"} key={`kid-${kidInnerIndex}`}>
        <label className="roomPassInfo-passenger-info-title">
          {t("completepassengers.kidpassangernum")} {kidInnerIndex + 1}
        </label>

        <div className="passenger-fields-grid passenger-fields-grid--minor">
          <div className="passenger-field passenger-field--firstname">
            {renderInput(
              "firstname",
              PASSENGER_KEYS.FIRSTNAME,
              kidInnerIndex,
              KIDS
            )}
          </div>

          <div className="passenger-field passenger-field--lastname">
            {renderInput(
              "lastname",
              PASSENGER_KEYS.LASTNAME,
              kidInnerIndex,
              KIDS
            )}
          </div>

          {useGenderDerivedLdsTitles ? (
            <div className="passenger-field passenger-field--gender">{renderGender(kidInnerIndex, KIDS)}</div>
          ) : null}

          {renderDatePickerInput(
            PASSENGER_KEYS.AGE,
            kidInnerIndex,
            KIDS,
            2,
            12,
            allDisabled
          )}
        </div>
      </div>
    ));
  };

  const renderInfants = () => {
    return passengerInfo?.infants.map((_, infantInnerIndex) => (
      <div className={"passenger-container"} key={`infant-${infantInnerIndex}`}>
        <label className="roomPassInfo-passenger-info-title">
          {t("completepassengers.infantpassangernum")} {infantInnerIndex + 1}
        </label>

        <div className="passenger-fields-grid passenger-fields-grid--minor">
          <div className="passenger-field passenger-field--firstname">
            {renderInput(
              "firstname",
              PASSENGER_KEYS.FIRSTNAME,
              infantInnerIndex,
              INFANTS
            )}
          </div>
          <div className="passenger-field passenger-field--lastname">
            {renderInput(
              "lastname",
              PASSENGER_KEYS.LASTNAME,
              infantInnerIndex,
              INFANTS
            )}
          </div>
          {renderDatePickerInput(
            PASSENGER_KEYS.AGE,
            infantInnerIndex,
            INFANTS,
            0,
            2,
            allDisabled
          )}
        </div>
      </div>
    ));
  };

  const renderPassengers = () => {
    // return null;
    return passengerInfo?.adults.map((_, adultInnerIndex) => (
      <div className={"passenger-container"} key={`adult-${adultInnerIndex}`}>
        <div className="passenger-card-header">
          <label className="roomPassInfo-passenger-info-title">
            {t("completepassengers.passangernum")} {adultInnerIndex + 1}
          </label>
          {(searchTab === PACKAGES || flightPaxFormMode) && adultInnerIndex === 0 && (
            <span className="primary-contact-badge" title={t("completepassengers.primarycontact")}>
              {t("completepassengers.primarycontact")}
            </span>
          )}
        </div>
        <div className="passenger-form passenger-form--adult">
          <div className="passenger-pax-row passenger-pax-row--primary">
            <div className="passenger-field passenger-field--gender-or-title">
              {useGenderDerivedLdsTitles
                ? renderGender(adultInnerIndex, ADULTS)
                : renderTitle(PASSENGER_KEYS.TITLE, adultInnerIndex, ADULTS)}
            </div>
            <div className="passenger-field passenger-field--firstname">
              {renderInput(
                "firstname",
                PASSENGER_KEYS.FIRSTNAME,
                adultInnerIndex,
                ADULTS
              )}
            </div>
            <div className="passenger-field passenger-field--lastname">
              {renderInput(
                "lastname",
                PASSENGER_KEYS.LASTNAME,
                adultInnerIndex,
                ADULTS
              )}
            </div>
            <div className="passenger-field passenger-field--phone">
              {renderInput("phone", PASSENGER_KEYS.CELL, adultInnerIndex, ADULTS)}
            </div>
          </div>
          <div className="passenger-pax-row passenger-pax-row--secondary">
            <div className="passenger-field passenger-field--email">
              {renderInput("email", PASSENGER_KEYS.EMAIL, adultInnerIndex, ADULTS)}
            </div>
            {renderDatePickerInput(
              PASSENGER_KEYS.AGE,
              adultInnerIndex,
              ADULTS,
              12,
              100,
              allDisabled
            )}
          </div>
        </div>
      </div>
    ));
  };

  return (
    <div
      className={
        "roomPassInfo-component roomPassInfo-component--package-pax" +
        (flightPaxFormMode ? " roomPassInfo-component--flight-pax" : "") +
        (roomCompositionError ? " passenger-field--has-validation-error" : "")
      }
      dir="rtl"
      style={{ paddingBottom: 0, paddingTop: 0, marginBottom: 20 }}
      data-lds-room-index={roomIndex}
    >
      <div className="roomPassInfo-component-item">
        {/* Room Header (מלון/חבילה); בטיסה בלבד — ללא אייקון/שם חדר */}
        <div className="roomPassInfo-main-title-wrapper">
          {!flightPaxFormMode ? (
            <div className="roomPassInfo-title-img-wrapper">
              <img className="roomPassInfo-title-img" src={roomImg} alt="" />
            </div>
          ) : null}

          {searchTab === PACKAGES && !flightPaxFormMode && roomName && String(roomName).trim() ? (
            <div className="roomPassInfo-hotel-name-wrapper">
              <div className="room-name-badge">
                <label className="roomPassInfo-hotel-name">{roomName}</label>
              </div>
            </div>
          ) : null}
        </div>

        {/* Title */}
        <div className="roomPassInfo-title-wrapper">
          <div className="roomPassInfo-border"></div>
          <label className="roomPassInfo-title">
            {flightPaxFormMode
              ? `${t("offerPageFlight.paxGroupLabel")} ${roomIndex + 1}`
              : `${t("completepassengers.room")} ${roomIndex + 1}`}
          </label>
          <div className="roomPassInfo-border"></div>
        </div>
        {roomCompositionError ? (
          <div className="passenger-field__validation-error roomPassInfo-composition-error">
            {roomCompositionError}
          </div>
        ) : null}

        {renderPassengers()}
        {renderKids()}
        {renderInfants()}
      </div>
    </div>
  );
};

export default PassengerData;
